import { Controller } from "@hotwired/stimulus"

/**
 * Controller for handling task drops on site sections.
 * Makes site sections valid drop targets for task moves.
 *
 * Dispatches 'plantafel:task-moved' event when a task is dropped.
 */
export default class extends Controller {
  static values = {
    siteId: Number,
  }

  connect() {
    this.element.addEventListener("dragover", this.handleDragOver.bind(this))
    this.element.addEventListener("dragenter", this.handleDragEnter.bind(this))
    this.element.addEventListener("dragleave", this.handleDragLeave.bind(this))
    this.element.addEventListener("drop", this.handleDrop.bind(this))
  }

  disconnect() {
    this.element.removeEventListener("dragover", this.handleDragOver.bind(this))
    this.element.removeEventListener("dragenter", this.handleDragEnter.bind(this))
    this.element.removeEventListener("dragleave", this.handleDragLeave.bind(this))
    this.element.removeEventListener("drop", this.handleDrop.bind(this))
  }

  handleDragOver(event) {
    // Only handle task drags, not resource drags
    if (!document.body.dataset.plantafelTaskDragging) {
      return
    }

    event.preventDefault()
    event.dataTransfer.dropEffect = "move"

    // Show position preview highlight in this row
    this.showPositionPreview(event)
  }

  /**
   * Show a visual preview of where the task will land when dropped.
   * Creates a ghost task bar in the target row at the calculated position.
   */
  showPositionPreview(event) {
    const dropZone = this.element.querySelector("[data-plantafel-site-drop-zone]")
    if (!dropZone) return

    // Only show preview if this is a valid drop target (different site)
    if (!dropZone.classList.contains("bg-teal-50")) return

    const rect = dropZone.getBoundingClientRect()
    const relativeX = event.clientX - rect.left
    const totalWidth = rect.width

    // Get view mode
    const plantafelContainer = document.querySelector('[data-controller="plantafel"]')
    const viewMode = plantafelContainer?.dataset.plantafelViewModeValue || "week"

    // Only show preview for 3_days and week views
    if (!["3_days", "week"].includes(viewMode)) return

    const numDays = viewMode === "3_days" ? 3 : 7
    const partsPerDay = 5
    const totalParts = numDays * partsPerDay

    // Calculate target position
    const dayWidth = totalWidth / numDays
    const dropDay = Math.max(0, Math.min(numDays - 1, Math.floor(relativeX / dayWidth)))
    const positionInDay = (relativeX - dropDay * dayWidth) / dayWidth
    const dropPart = Math.max(0, Math.min(4, Math.floor(positionInDay * partsPerDay)))

    // Get the dragged task element to copy its appearance
    const draggedTaskId = document.body.dataset.plantafelDraggedTaskId
    const draggedTask = draggedTaskId
      ? document.querySelector(`[data-task-id="${draggedTaskId}"]`)
      : null

    if (!draggedTask) return

    // Get task duration from data attributes
    const origStartDay = parseInt(draggedTask.dataset.plantafelTaskDragStartDayValue || "0")
    const origStartPart = parseInt(draggedTask.dataset.plantafelTaskDragStartPartValue || "1") - 1 // Convert to 0-indexed
    const origEndDay = parseInt(draggedTask.dataset.plantafelTaskDragEndDayValue || "0")
    const origEndPart = parseInt(draggedTask.dataset.plantafelTaskDragEndPartValue || "1") - 1 // Convert to 0-indexed

    // Calculate duration in parts
    const origStartTotal = origStartDay * partsPerDay + origStartPart
    const origEndTotal = origEndDay * partsPerDay + origEndPart
    const durationInParts = Math.max(1, origEndTotal - origStartTotal + 1)

    // Calculate new position
    const newStartPart = dropDay * partsPerDay + dropPart
    const newEndPart = Math.min(totalParts - 1, newStartPart + durationInParts - 1)

    // Calculate left and width percentages
    const leftPercent = (newStartPart / totalParts) * 100
    const widthPercent = ((newEndPart - newStartPart + 1) / totalParts) * 100

    // Get the task bar element to copy its style
    const taskBar = draggedTask.querySelector('[data-plantafel-resize-target="bar"]')
    const backgroundColor = taskBar ? getComputedStyle(taskBar).backgroundColor : "rgb(34, 197, 94)"

    // Create or update the preview ghost element
    this.createOrUpdatePreviewGhost(
      dropZone,
      leftPercent,
      widthPercent,
      backgroundColor,
      draggedTask,
    )

    // Hide the original task's transform preview since we're showing it here
    if (taskBar) {
      taskBar.style.opacity = "0.3"
      taskBar.style.transform = ""
    }
  }

  /**
   * Create or update the ghost preview element in the target row.
   */
  createOrUpdatePreviewGhost(dropZone, leftPercent, widthPercent, backgroundColor, draggedTask) {
    // Find or create the preview container
    let previewContainer = dropZone.querySelector(".plantafel-drop-preview")
    if (!previewContainer) {
      previewContainer = document.createElement("div")
      previewContainer.className =
        "plantafel-drop-preview absolute inset-0 pointer-events-none z-30"
      dropZone.appendChild(previewContainer)
    }

    // Find or create the ghost element
    let ghost = previewContainer.querySelector(".plantafel-drop-ghost")
    if (!ghost) {
      ghost = document.createElement("div")
      ghost.className =
        "plantafel-drop-ghost absolute top-1 bottom-1 rounded transition-all duration-75"
      ghost.style.opacity = "0.7"
      ghost.style.border = "2px dashed rgba(0, 0, 0, 0.3)"
      previewContainer.appendChild(ghost)
    }

    // Update ghost position and size
    ghost.style.left = `${leftPercent}%`
    ghost.style.width = `${widthPercent}%`
    ghost.style.backgroundColor = backgroundColor

    // Copy task name if available
    const taskName = draggedTask.dataset.taskName
    if (taskName && !ghost.querySelector(".ghost-label")) {
      const label = document.createElement("span")
      label.className = "ghost-label text-xs text-black truncate px-2 flex items-center h-full"
      label.textContent = taskName
      ghost.appendChild(label)
    }
  }

  /**
   * Remove the ghost preview element.
   */
  removePreviewGhost() {
    const dropZone = this.element.querySelector("[data-plantafel-site-drop-zone]")
    if (!dropZone) return

    const previewContainer = dropZone.querySelector(".plantafel-drop-preview")
    if (previewContainer) {
      previewContainer.remove()
    }

    // Restore the original task's opacity
    const draggedTaskId = document.body.dataset.plantafelDraggedTaskId
    if (draggedTaskId) {
      const draggedTask = document.querySelector(`[data-task-id="${draggedTaskId}"]`)
      if (draggedTask) {
        const taskBar = draggedTask.querySelector('[data-plantafel-resize-target="bar"]')
        if (taskBar) {
          taskBar.style.opacity = ""
        }
      }
    }
  }

  handleDragEnter(event) {
    // Only handle task drags
    if (!document.body.dataset.plantafelTaskDragging) {
      return
    }

    event.preventDefault()
  }

  handleDragLeave(event) {
    // Only handle task drags
    if (!document.body.dataset.plantafelTaskDragging) {
      return
    }

    // Only remove highlight if we're actually leaving the element
    if (!this.element.contains(event.relatedTarget)) {
      // Clear zone position preview when leaving
      this.clearZoneHighlights()
      this.removePreviewGhost()
    }
  }

  handleDrop(event) {
    // Only handle task drags
    if (!document.body.dataset.plantafelTaskDragging) {
      return
    }

    event.preventDefault()

    // Clear zone highlights and preview ghost
    this.clearZoneHighlights()
    this.removePreviewGhost()

    // Parse drag data
    const jsonString = event.dataTransfer.getData("application/json")
    if (!jsonString) {
      return
    }

    let data
    try {
      data = JSON.parse(jsonString)
    } catch (_e) {
      // Invalid JSON in drag data - silently ignore
      return
    }

    // Verify this is a task move drag
    if (data.dragType !== "task-move") {
      return
    }

    const { taskId, currentSiteId, taskName } = data
    const toSiteId = this.siteIdValue

    // Validate: can't drop on same site
    if (currentSiteId === toSiteId.toString()) {
      return
    }

    // Calculate drop position for combined site + position change
    const position = this.calculateDropPosition(event, data)

    // Set flag BEFORE dispatching so task_drag_controller.handleDragEnd
    // knows the drop was handled by site_drop and shouldn't save separately
    document.body.dataset.plantafelSiteDropHandled = "true"

    // Dispatch custom event for main controller to handle
    const moveEvent = new CustomEvent("plantafel:task-moved", {
      bubbles: true,
      detail: {
        taskId,
        fromSiteId: currentSiteId,
        toSiteId,
        taskName,
        // Include position data for combined site + time repositioning
        startDay: position?.startDay,
        startPart: position?.startPart,
        endDay: position?.endDay,
        endPart: position?.endPart,
        hasPosition: position !== null,
      },
    })

    this.element.dispatchEvent(moveEvent)
  }

  /**
   * Calculate grid position from drop coordinates.
   * Preserves task duration when calculating new end position.
   * Only works for 3_days and week view modes.
   */
  calculateDropPosition(event, dragData) {
    const dropZone = this.element.querySelector("[data-plantafel-site-drop-zone]")
    if (!dropZone) return null

    const rect = dropZone.getBoundingClientRect()
    const relativeX = event.clientX - rect.left
    const totalWidth = rect.width

    // Get view mode from plantafel controller
    const plantafelContainer = document.querySelector('[data-controller="plantafel"]')
    const viewMode = plantafelContainer?.dataset.plantafelViewModeValue || "week"

    // Only calculate position for 3_days and week views (not month)
    if (!["3_days", "week"].includes(viewMode)) {
      return null
    }

    const numDays = viewMode === "3_days" ? 3 : 7
    const partsPerDay = 5

    // Calculate which day and part the drop occurred in
    const dayWidth = totalWidth / numDays
    const dropDay = Math.max(0, Math.min(numDays - 1, Math.floor(relativeX / dayWidth)))
    const positionInDay = (relativeX - dropDay * dayWidth) / dayWidth
    const dropPart = Math.max(1, Math.min(5, Math.floor(positionInDay * partsPerDay) + 1))

    // Get original task duration from drag data
    const origStartDay = parseInt(dragData.startDay || "0")
    const origStartPart = parseInt(dragData.startPart || "1")
    const origEndDay = parseInt(dragData.endDay || "0")
    const origEndPart = parseInt(dragData.endPart || "1")

    // Calculate original duration in parts
    const origStartTotal = origStartDay * partsPerDay + origStartPart
    const origEndTotal = origEndDay * partsPerDay + origEndPart
    const durationInParts = Math.max(1, origEndTotal - origStartTotal)

    // Calculate new end position preserving duration
    const newStartTotal = dropDay * partsPerDay + dropPart
    const newEndTotal = newStartTotal + durationInParts

    // Convert back to day + part
    const newEndDay = Math.floor((newEndTotal - 1) / partsPerDay)
    const newEndPart = ((newEndTotal - 1) % partsPerDay) + 1

    return {
      startDay: dropDay,
      startPart: dropPart,
      endDay: newEndDay,
      endPart: newEndPart,
    }
  }

  /**
   * Clear any zone highlight previews.
   */
  clearZoneHighlights() {
    const highlightLayer = this.element.querySelector(
      '[data-plantafel-task-section-target="highlightLayer"]',
    )
    if (!highlightLayer) return

    highlightLayer.querySelectorAll(".zone-highlight").forEach((zone) => {
      zone.classList.remove("bg-teal-200", "bg-teal-100")
    })
  }
}
